/**
 * Dialog for displaying QR code and payment link
 */
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Check, Download, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const QRCodeDialog = ({
  open,
  onOpenChange,
  selectedRequest,
  onShare
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Link copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadQR = () => {
    const svg = document.getElementById('qr-code-svg');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      const link = document.createElement('a');
      link.download = `parachpay-qr-${selectedRequest?.id}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center">Payment Request</DialogTitle>
          <DialogDescription className="text-center">
            {selectedRequest?.amount 
              ? `$${selectedRequest.amount.toFixed(2)} ${selectedRequest.currency}`
              : 'Any amount'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex flex-col items-center py-6">
          {/* QR Code */}
          <div className="p-4 bg-white rounded-2xl shadow-lg mb-4" data-testid="qr-code-container">
            <QRCodeSVG
              id="qr-code-svg"
              value={selectedRequest?.qr_data || ''}
              size={200}
              level="H"
              includeMargin
            />
          </div>

          {selectedRequest?.note && (
            <p className="text-center text-muted-foreground mb-4 px-4">"{selectedRequest.note}"</p>
          )}

          {/* Payment Link */}
          <div className="w-full space-y-3">
            <div className="flex gap-2">
              <Input
                value={selectedRequest?.payment_link || ''}
                readOnly
                className="font-mono text-xs rounded-xl"
                data-testid="payment-link-input"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => handleCopyLink(selectedRequest?.payment_link)}
                className="rounded-xl shrink-0"
                data-testid="copy-link-btn"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button 
                variant="outline" 
                onClick={handleDownloadQR}
                className="rounded-xl"
                data-testid="download-qr-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download QR
              </Button>
              <Button 
                onClick={() => onShare(selectedRequest)}
                className="rounded-xl"
                data-testid="share-qr-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QRCodeDialog;
